/**
 * OF-006 — Provider Facility Media Page
 *
 * Single-purpose page where a clinic admin uploads facility/building/
 * interior photos that appear on the patient-facing provider profile.
 *
 * Strict canonical authority:
 *   - Writer:   POST /api/clinic/media     (context_type='clinic_profile', context_id=clinic_id)
 *   - Reader:   GET  /api/clinic/media?context_type=clinic_profile
 *   - Cover:    POST /api/clinic/media/{media_id}/cover
 *   - Reorder:  POST /api/clinic/media/reorder
 *   - Delete:   DELETE /api/clinic/media/{media_id}   (soft delete)
 *   - Read:     GET  /api/media/{media_id}?variant=preview|thumbnail
 *
 * Moderation: all newly uploaded items land at moderation_status='pending'
 * and require TreatX admin approval before they appear publicly. Cover
 * must be approved.
 *
 * NO parallel media surface. NO bypass. NO shadow collection.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../../utils/api';
import {
  Upload, CheckCircle2, Clock, AlertTriangle, RefreshCw, ImageIcon,
  Star, StarOff, ArrowUp, ArrowDown, Trash2,
} from 'lucide-react';
import AuthMediaThumb from '../../../components/AuthMediaThumb';

// Canonical moderation status → i18n key + visual config. Backend enum
// values (`pending`/`approved`/`rejected`/`superseded`) remain the SoT;
// only display chrome is localized.
const STATUS_BADGE_CFG = {
  pending:    { key: 'pending',    cls: 'bg-amber-100 text-amber-800',     Icon: Clock },
  approved:   { key: 'approved',   cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  rejected:   { key: 'rejected',   cls: 'bg-rose-100 text-rose-800',       Icon: AlertTriangle },
  superseded: { key: 'superseded', cls: 'bg-slate-200 text-slate-600',     Icon: RefreshCw },
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const cfg = STATUS_BADGE_CFG[status] || STATUS_BADGE_CFG.pending;
  const Icon = cfg.Icon;
  const label = t(`provider.media.facility.statuses.${cfg.key}`);
  return (
    <Badge className={`${cfg.cls} font-medium text-[10px]`} data-testid={`fm-status-${status || 'unknown'}`}>
      <Icon className="h-3 w-3 mr-1" />
      {label}
    </Badge>
  );
}

function AuthThumb({ mediaId }) {
  return (
    <AuthMediaThumb
      mediaId={mediaId}
      alt=""
      variants={['thumbnail', 'preview', 'original']}
      className="w-full h-full object-cover"
      testIdPrefix="fm-thumb"
    />
  );
}

export default function ClinicFacilityMediaPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clinic/media', {
        params: { context_type: 'clinic_profile' },
      });
      const list = (res.data?.media || []).filter(m => !m.deleted_at);
      list.sort((a, b) => {
        if (!!b.is_cover - !!a.is_cover) return !!b.is_cover - !!a.is_cover;
        const ao = typeof a.order_index === 'number' ? a.order_index : 1e6;
        const bo = typeof b.order_index === 'number' ? b.order_index : 1e6;
        if (ao !== bo) return ao - bo;
        return (b.created_at || '').localeCompare(a.created_at || '');
      });
      setItems(list);
    } catch {
      toast.error(t('provider.media.facility.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(t('provider.media.facility.errors.tooLarge')); return; }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error(t('provider.media.facility.errors.unsupported'));
      return;
    }
    setUploading(true);
    try {
      const cid = (await api.get('/clinic/logo/current')).data?.logo?.owner_id;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('context_type', 'clinic_profile');
      fd.append('context_id', cid || '');
      const res = await api.post('/clinic/media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        toast.success(t('provider.media.facility.toasts.submitted'));
        await refresh();
      } else {
        toast.error(res.data?.message || res.data?.error || t('provider.media.facility.errors.uploadFailed'));
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || t('provider.media.facility.errors.uploadFailed');
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const setCover = async (media_id) => {
    try {
      const res = await api.post(`/clinic/media/${media_id}/cover`);
      if (res.data?.success) {
        toast.success(t('provider.media.facility.toasts.coverSet'));
        refresh();
      } else {
        toast.error(res.data?.message || res.data?.error || t('provider.media.facility.errors.setCoverFailed'));
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || t('provider.media.facility.errors.setCoverFailed');
      toast.error(msg);
    }
  };

  const move = async (idx, delta) => {
    const next = [...items];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    try {
      const ids = next.map(m => m.media_id);
      await api.post('/clinic/media/reorder', { ordered_media_ids: ids });
    } catch {
      toast.error(t('provider.media.facility.errors.reorderFailed'));
      refresh();
    }
  };

  const remove = async (media_id) => {
    if (!window.confirm(t('provider.media.facility.removeConfirm'))) return;
    try {
      await api.delete(`/clinic/media/${media_id}`);
      toast.success(t('provider.media.facility.toasts.removed'));
      refresh();
    } catch {
      toast.error(t('provider.media.facility.errors.removeFailed'));
    }
  };

  const cover = items.find(m => m.is_cover);
  const pendingCount = items.filter(m => m.moderation_status === 'pending').length;
  const rejectedCount = items.filter(m => m.moderation_status === 'rejected').length;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="clinic-facility-media-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t('provider.media.facility.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('provider.media.facility.subtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="fm-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> {t('provider.media.facility.refresh')}
        </Button>
      </div>

      <Card data-testid="fm-upload-card">
        <CardContent className="p-5 flex items-center gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
            data-testid="fm-upload-input"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="fm-upload-btn"
          >
            <Upload className="h-4 w-4 mr-1" />
            {uploading ? t('provider.media.facility.uploading') : t('provider.media.facility.add')}
          </Button>
          <div className="text-xs text-slate-500">
            {t('provider.media.facility.constraints')}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span>{t('provider.media.facility.imageCount', { count: items.length })}</span>
        {pendingCount > 0 && <span data-testid="fm-pending-count">{t('provider.media.facility.pendingCount', { count: pendingCount })}</span>}
        {rejectedCount > 0 && <span data-testid="fm-rejected-count" className="text-rose-700">{t('provider.media.facility.rejectedCount', { count: rejectedCount })}</span>}
        {cover && <span className="text-emerald-700">{t('provider.media.facility.coverSet')}</span>}
      </div>

      {items.length === 0 && !loading && (
        <div className="text-center py-16 border-dashed border-2 border-slate-200 rounded-lg" data-testid="fm-empty-state">
          <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">{t('provider.media.facility.empty')}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m, idx) => (
          <Card
            key={m.media_id}
            className={`overflow-hidden ${m.is_cover ? 'ring-2 ring-emerald-500' : ''}`}
            data-testid={`fm-card-${m.media_id}`}
          >
            <div className="aspect-video bg-slate-100">
              <AuthThumb mediaId={m.media_id} />
            </div>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <StatusBadge status={m.moderation_status} />
                {m.is_cover && (
                  <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" data-testid={`fm-cover-badge-${m.media_id}`}>
                    <Star className="h-3 w-3 mr-1" />{t('provider.media.facility.coverBadge')}
                  </Badge>
                )}
              </div>
              {m.moderation_notes && m.moderation_status === 'rejected' && (
                <p className="text-[11px] text-rose-700 italic" data-testid={`fm-reject-note-${m.media_id}`}>
                  {m.moderation_notes}
                </p>
              )}
              <div className="flex items-center gap-1 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => setCover(m.media_id)}
                  disabled={m.is_cover || m.moderation_status !== 'approved'}
                  data-testid={`fm-set-cover-${m.media_id}`}
                  title={m.moderation_status !== 'approved' ? t('provider.media.facility.coverOnlyApproved') : t('provider.media.facility.setAsCover')}
                >
                  {m.is_cover ? <StarOff className="h-3 w-3" /> : <Star className="h-3 w-3" />}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`fm-move-up-${m.media_id}`}
                >
                  <ArrowUp className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1}
                  data-testid={`fm-move-down-${m.media_id}`}
                >
                  <ArrowDown className="h-3 w-3" />
                </Button>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-[11px] text-rose-600 hover:bg-rose-50"
                  onClick={() => remove(m.media_id)}
                  data-testid={`fm-delete-${m.media_id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
